import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { SafeStorage } from '../utils/safeStorage';
import { BusinessService } from '../services/BusinessService';
import type { PurchaseCheck } from '../types/business';

/**
 * The leave/return half of purchase tracking (§7).
 *
 * THE ARMING FLAG IS THE WHOLE DESIGN
 * ───────────────────────────────────
 * A bare "the app came back to the foreground" listener would fire on every
 * task-switch, phone call and notification tap, and would ask people whether
 * they had completed a purchase they never started. So the listener only acts
 * when this flag is set, and only a Store-link tap sets it (armPurchaseReturn,
 * called immediately before the link opens). Answering or dismissing the prompt
 * clears it.
 *
 * The flag is in SafeStorage rather than a module variable because iOS will
 * discard the web view of a backgrounded app: someone who taps out, buys, and
 * comes back an hour later gets a cold start, and a module variable would be
 * gone. localStorage survives that, so the prompt still fires.
 *
 * There is deliberately NO expiry window. Tap out on Monday, buy on Thursday,
 * open the app on Friday — the pending check is still there and still asked
 * about. That is per spec, and it is also the honest behaviour: people do not
 * buy on the timescale an app session lasts.
 *
 * ON WEB there is no appStateChange, so this uses the equivalent DOM signals:
 * `visibilitychange` back to visible, plus `focus` for the case where the shop
 * opened in a new tab and the user simply clicked back to this one.
 */

const ARMED_KEY = 'business_purchase_return_armed_v1';

/** Call immediately before opening a Store purchase link. */
export function armPurchaseReturn(checkId: string): void {
  if (!checkId) return;
  SafeStorage.setItem(ARMED_KEY, checkId);
}

export function disarmPurchaseReturn(): void {
  SafeStorage.removeItem(ARMED_KEY);
}

export function isPurchaseReturnArmed(): boolean {
  return !!SafeStorage.getItem(ARMED_KEY);
}

/**
 * Watches for the user's return after a Store-link tap and surfaces the pending
 * check, if there is one. Mount once, high in the tree.
 *
 * Returns the check to prompt about and a resolver; render is the caller's job
 * (PurchaseCheckPrompt).
 */
export function usePurchaseReturn(userId: string | null | undefined) {
  const [pendingCheck, setPendingCheck] = useState<PurchaseCheck | null>(null);
  // Guards against two resume signals racing (visibilitychange and focus both
  // fire on a web tab switch) and re-running the lookup while one is in flight.
  const checkingRef = useRef(false);

  useEffect(() => {
    if (!userId) return;
    let active = true;

    const lookUpPendingCheck = async () => {
      if (!active || checkingRef.current) return;
      if (!isPurchaseReturnArmed()) return;
      checkingRef.current = true;
      try {
        const check = await BusinessService.getPendingCheck(userId);
        if (!active) return;
        if (check) {
          setPendingCheck(check);
        } else {
          // Armed with nothing to ask about — the check was answered elsewhere,
          // or never landed. Clear the flag so it does not sit armed forever.
          disarmPurchaseReturn();
        }
      } finally {
        checkingRef.current = false;
      }
    };

    // Cold start after the OS discarded the web view: the resume event already
    // happened before this listener existed, so check once on mount too.
    void lookUpPendingCheck();

    const cleanups: Array<() => void> = [];

    if (Capacitor.isNativePlatform()) {
      // Native: the app is genuinely backgrounded when the system browser takes
      // over (see the note in utils/openExternal.ts on why window.open rather
      // than @capacitor/browser), so appStateChange is the real return signal.
      (async () => {
        try {
          const { App } = await import('@capacitor/app');
          const handle = await App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) void lookUpPendingCheck();
          });
          if (!active) { handle.remove(); return; }
          cleanups.push(() => handle.remove());
        } catch (e) {
          console.warn('[usePurchaseReturn] Capacitor App listener unavailable:', e);
        }
      })();
    } else {
      const onVisible = () => {
        if (document.visibilityState === 'visible') void lookUpPendingCheck();
      };
      const onFocus = () => void lookUpPendingCheck();
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', onFocus);
      cleanups.push(() => document.removeEventListener('visibilitychange', onVisible));
      cleanups.push(() => window.removeEventListener('focus', onFocus));
    }

    return () => {
      active = false;
      cleanups.forEach(fn => fn());
    };
  }, [userId]);

  /** Answer the prompt. Disarms either way — the question has been asked. */
  const respond = async (confirmed: boolean) => {
    const check = pendingCheck;
    setPendingCheck(null);
    disarmPurchaseReturn();
    if (check) await BusinessService.respondToCheck(check.id, confirmed);
  };

  /**
   * Dismissed without answering. The check stays pending in Firestore but the
   * flag is cleared, so it is not re-asked on every subsequent resume — someone
   * who closed the question has answered it in the only way that matters.
   */
  const dismiss = () => {
    setPendingCheck(null);
    disarmPurchaseReturn();
  };

  return { pendingCheck, respond, dismiss };
}
